import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  ButtonIcon,
  Icon,
  MobileDrawer,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import {
  getAccessRequestHistoryData,
  setIsARSModalLoading,
  setIsARSModalOpen,
} from '../../redux/certdetails/action';
import {
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../utils/constant';
import { useInvertedLocalize } from '../../utils/localization';
import { getMessageKey } from '../../utils/utils';

import AccessRequestHistoryBody from './AccessRequestHistoryBody';
import AccessRequestHistorySubTitle from './AccessRequestHistorySubTitle';
import {
  ARS_API_SUFFIX_FOR_ACCOUNT,
  ARS_API_SUFFIX_FOR_ENTITLEMENT,
  ARS_API_SUFFIX_FOR_ROLE,
  REQUEST_TYPE_ACCOUNT,
  REQUEST_TYPE_ENTITLEMENT,
  REQUEST_TYPE_ROLE,
} from './constant';
import invertedMessages from './messages';
import { getArsKind } from './utils';

import './AccessRequestHistory.css';

function AccessRequestHistory() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const l = useInvertedLocalize(invertedMessages);
  const {
    global: { mediaQueryValue, globalDateFormat, certKey },
    accessRequestHistory: {
      arsData,
      arsTaskPayload,
      isOpen,
      isLoading: isArsLoading,
      isError: isArsError,
    },
  } = useSelector((state) => state?.certifications?.details);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );

  const isMobileWidth = mobileXlargeQuery.value <= mediaQueryValue;

  // TODO: This needs to be checked later
  // const { messages } = useSelector((state) => state?.user);
  // const modalTitle =
  //   messages[arsData?.requestAccessHistoryHeaderLabelKey] ||
  //   arsData?.requestAccessHistoryHeaderLabelValue ||
  //   'Access Request History';
  const modalTitle = localize(intl, getMessageKey('ARS.TITLE'));

  const callApiForArs = () => {
    const arsKind = getArsKind(arsTaskPayload);

    if (arsKind === REQUEST_TYPE_ROLE) {
      const payload = {
        cert_rolekey: arsTaskPayload?.CERT_ROLEKEY,
        cert_userkey: arsTaskPayload?.CERT_USERKEY,
      };

      dispatch(
        getAccessRequestHistoryData(certKey, ARS_API_SUFFIX_FOR_ROLE, payload)
      );
    }

    if (arsKind === REQUEST_TYPE_ACCOUNT) {
      const payload = {
        cert_accountkey: arsTaskPayload?.CERT_ACCOUNTKEY,
        cert_userkey: arsTaskPayload?.CERT_USERKEY,
      };

      dispatch(
        getAccessRequestHistoryData(
          certKey,
          ARS_API_SUFFIX_FOR_ACCOUNT,
          payload
        )
      );
    }

    if (arsKind === REQUEST_TYPE_ENTITLEMENT) {
      const payload = { arstaskkey: arsTaskPayload?.ARSTASKKEY };

      dispatch(
        getAccessRequestHistoryData(
          certKey,
          ARS_API_SUFFIX_FOR_ENTITLEMENT,
          payload
        )
      );
    }
  };

  useEffect(() => {
    setIsLoading(isArsLoading);
  }, [isArsLoading]);

  useEffect(() => {
    setIsError(isArsError);
  }, [isArsError]);

  useEffect(() => {
    if (isOpen && isLoading) callApiForArs();
  }, [isOpen, isLoading]);

  const closeAccessRequestHistoryModal = () => {
    // Don't need to dispatch setIsARSModalLoading as when API is called it is already set to true
    dispatch(setIsARSModalOpen(false, null));
  };

  const reTriggerAccessRequestHistoryModal = () => {
    dispatch(setIsARSModalLoading(true));
  };

  const messageKeys = {
    ...arsData?.requestAccessAttributeMessageKeys,
    ...arsData?.arsRequestMapMessageKeys,
    ...arsData?.arsTaskMessageKeys,
    ...arsData?.messageKeys,
  };

  // TODO: To fix why the modal is going behind the carousel till then this useEffect is kept here
  useEffect(() => {
    closeAccessRequestHistoryModal();
  }, [isMobileWidth]);

  if (isMobileWidth) {
    const getHeaderContent = () => (
      <>
        <div className='AccessRequestHistoryModal-mobileResponsiveHeaderText'>
          <Typography kind='h1'>{modalTitle}</Typography>
          <AccessRequestHistorySubTitle
            isError={isError}
            isLoading={isLoading}
            data={
              {
                ...arsData?.requestAccessAttributeMap,
                ...(arsData?.requestHistoryFound
                  ? { requestId: arsData?.requestId }
                  : {}),
              } || {}
            }
            messageKeys={messageKeys || {}}
          />
        </div>
        <ButtonIcon
          size='medium'
          kind='ghost'
          icon={<Icon kind='Close' />}
          onClick={closeAccessRequestHistoryModal}
          ariaLabel={l('Close')}
        />
      </>
    );

    // TODO: @Jacob, @Sagar,  Needs to be tested with DATA, Jacob is not sure how to load any
    return (
      <MobileDrawer
        isOpen={isOpen}
        setIsOpen={closeAccessRequestHistoryModal}
        className='AccessRequestHistoryModal-mobileResponsive'
        headerContent={getHeaderContent()}
        content={
          <AccessRequestHistoryBody
            isError={isError}
            isLoading={isLoading}
            data={arsData}
            arsTaskPayload={arsTaskPayload}
            closeAccessRequestHistoryModal={closeAccessRequestHistoryModal}
            reTriggerAccessRequestHistoryModal={
              reTriggerAccessRequestHistoryModal
            }
            messageKeys={messageKeys || {}}
            globalDateFormat={globalDateFormat}
          />
        }
      />
    );
  }

  return (
    <ModalIsland
      className='AccessRequestHistoryModal'
      onClose={closeAccessRequestHistoryModal}
      isOpen={isOpen}
      title={modalTitle}
      subtitle={
        <AccessRequestHistorySubTitle
          isError={isError}
          isLoading={isLoading}
          data={
            {
              ...arsData?.requestAccessAttributeMap,
              ...(arsData?.requestHistoryFound
                ? { requestId: arsData?.requestId }
                : {}),
            } || {}
          }
          messageKeys={messageKeys || {}}
        />
      }
      contentBody={
        <AccessRequestHistoryBody
          isError={isError}
          isLoading={isLoading}
          data={arsData}
          arsTaskPayload={arsTaskPayload}
          closeAccessRequestHistoryModal={closeAccessRequestHistoryModal}
          reTriggerAccessRequestHistoryModal={
            reTriggerAccessRequestHistoryModal
          }
          messageKeys={messageKeys || {}}
          globalDateFormat={globalDateFormat}
        />
      }
    />
  );
}

export default AccessRequestHistory;
